// app-api HTTP fetcher (M2 slice 4).
//
// One place that knows how to talk to app-api: the base URL, and turning a
// non-200 into a typed error. Widgets never call this directly — they go
// through `useWidgetData` (useWidgetData.ts), which wraps it in React Query.
//
// AUTH (WS-D D2): this is the single spot that attaches the user's bearer token.
// We send the Cognito **ID token** — its `aud` = the app-client id, exactly what
// the D3 JWT authorizer will validate, and it carries the email AuthContext
// reads (see contexts/AuthContext.ts). The attach is best-effort: no session ⇒
// no header, so this is inert until D3 flips the routes to `auth: jwt`
// (endpoints are still `auth: none` today). On unconfigured builds (local dev,
// CI/VRT) `fetchAuthSession` has nothing and we send no header — unchanged.
//
// BASE URL: `VITE_APP_API_URL` is injected at build time by the StaticSite
// (infra/app-web.ts) for deployed builds, and from a gitignored `.env.local`
// for `npm run dev`. It can rotate, so it is never hardcoded. Missing/blank ⇒
// we throw a clear setup error rather than fetching `undefined/...`.

import { AMPLIFY_CONFIGURED } from "../lib/amplify.js";

/** A non-2xx response (or a network failure) from app-api. */
export class ApiError extends Error {
  constructor(
    message: string,
    /** HTTP status, or 0 for a network/transport failure. */
    readonly status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** The user's ID token as a bearer header, or `{}` when there's no session
 *  (unconfigured build, or anonymous). Best-effort — never throws. */
async function authHeader(): Promise<Record<string, string>> {
  if (!AMPLIFY_CONFIGURED) return {};
  try {
    const { fetchAuthSession } = await import("aws-amplify/auth");
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** The app-api origin, without a trailing slash. Throws if unconfigured. */
function baseUrl(): string {
  const raw = import.meta.env.VITE_APP_API_URL;
  if (!raw) {
    throw new ApiError(
      "VITE_APP_API_URL is not set — point it at the app-api URL (see .env.local).",
      0
    );
  }
  return raw.replace(/\/+$/, "");
}

/**
 * GET `path` (a leading-slash path like `/widgets/injuries-suspension?...`) and
 * parse the JSON body. Throws {@link ApiError} on a non-2xx status or a
 * transport failure — React Query surfaces it as `isError` and can retry.
 */
export async function apiGet<T>(path: string): Promise<T> {
  const headers = await authHeader();
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}${path}`, { headers });
  } catch {
    // DNS/offline/CORS — no HTTP status to report.
    throw new ApiError(`Network request to ${path} failed`, 0);
  }
  if (!res.ok) {
    throw new ApiError(`GET ${path} → ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

/**
 * Send `body` as JSON to `path` with `method` (PUT/POST/PATCH/DELETE) and parse
 * the JSON response. Same auth as {@link apiGet} — the ID token rides along, so
 * these hit the D3 `auth: jwt` routes (e.g. saved boards, D5). Throws
 * {@link ApiError} on a non-2xx status or a transport failure.
 */
export async function apiSend<T>(
  method: "PUT" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: unknown
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(await authHeader()),
  };
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(`Network request to ${path} failed`, 0);
  }
  if (!res.ok) {
    throw new ApiError(`${method} ${path} → ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}
